import { useEffect, useState } from 'react';
import axios from 'axios';

import type { CoveredItemRow, StatusType } from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import { useClaim } from '~/components/ClaimContainer';
import { useMinimizedDialogs } from '~/components/core/MinimizedBar/Context';
import { closeExposure, fetchExposuresAndTotalAmounts } from '~/components/exposures/ExposureActions';
import { getExposureClosingDisabledReasonSimple } from '~/components/exposures/ExposureUtils';
import { MinimizedPaymentRequestContainer } from '~/components/exposures/PaymentRequestContainer/PaymentRequestContainer';
import useFetchExposures from '~/components/exposures/useFetchExposures';
import useExposureClosingReasons from '~/components/hooks/useExposureClosingReasons';
import { useInvoices } from '~/components/invoices/hooks/useInvoices';
import type { ExposureModel } from '~/components/types/exposure-types';
import type { PrepareExposureForAggregativePaymentResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError, reportGenericException } from '~/Utils';

interface ApplyInvoiceItemReturn {
  status: StatusType;
  message?: string;
}

interface ApplyInvoiceItemProps {
  item: CoveredItemRow;
  documentId?: number;
  claimDocumentSourceId?: string;
  onUpdate: ({ status, message }: ApplyInvoiceItemReturn) => void;
}

interface ApplyCoveragePaymentProps {
  coverageId: number;
  onPaymentDone: () => void;
}

interface UseCmsEmbeddedReturn {
  applyInvoiceItem: (props: ApplyInvoiceItemProps) => Promise<void>;
  applyCoveragePayment: (props: ApplyCoveragePaymentProps) => Promise<void>;
  coInsuranceRates: Record<string, number>;
  useFetchExposuresProxy: typeof useFetchExposures; // Remove this once we decide "Clive Embedded" is just "CMS"
}

const openPaymentDialog = ({
  add,
  claim,
  exposure,
  payableType = 'indemnity',
  onUpdate,
}: {
  // eslint-disable-next-line
  add: (props: any) => { handleCloseDialog: () => void };
  claim: { id: number };
  // eslint-disable-next-line
  exposure: Record<string, any>;
  payableType?: string;
  onUpdate: unknown;
}) => {
  const payableWithReserve = exposure[payableType];

  const paymentRequestDialogProps = {
    claim,
    exposure,
    payableWithReserve,
    payableType,
    onUpdate,
    cardDialogProps: {
      isDialog: true,
      maxWidth: 'md',
      fullWidth: true,
    },
    defaultAmount: payableWithReserve.reserve,
  };

  if (payableWithReserve.reserve === 0) {
    return;
  }

  const openPaymentRequestDialog = () => {
    const { handleCloseDialog } = add({
      barHeader: 'New Payment Request',
      type: 'PAYMENT',
      dialogComponent: MinimizedPaymentRequestContainer,
      dialogProps: {
        ...paymentRequestDialogProps,
        disableMinimized: true,
        onClose: () => handleCloseDialog(),
      },
    });
  };
  openPaymentRequestDialog();
  return;
};

const getCoInsuranceRates = async (claimId: number): Promise<Record<string, number>> => {
  try {
    const { data } = await axios.get(
      `/api/v1/claims/${claimId}/ai_embedded_recommendations/invoice_analysis/co_insurance_rates`
    );
    return data;
  } catch (error) {
    await reportAxiosError(error, false);
  }

  return {};
};

export const useCmsEmbedded = (): UseCmsEmbeddedReturn => {
  const [coInsuranceRates, setCoInsuranceRates] = useState<Record<string, number>>({});
  const { claim, onClaimUpdate } = useClaim({ claimRequired: true });
  const { getSingleInvoiceCoveragesBreakdown, prepareExposureForPayment } = useInvoices();
  const { add } = useMinimizedDialogs();

  // Get available closing reasons at the hook level
  const { possibleReasons: availableClosingReasons } = useExposureClosingReasons(claim, claim.exposures || []);

  useEffect(() => {
    const getData = async () => {
      const data = await getCoInsuranceRates(claim.id);
      setCoInsuranceRates(data);
    };
    getData();
  }, [claim.id]);

  const findExposure = async (filterFn: (exposure: ExposureModel) => boolean): Promise<ExposureModel | undefined> => {
    const exposures = (await fetchExposuresAndTotalAmounts(claim.id))?.exposures || [];
    return exposures.find((exp): exp is ExposureModel => filterFn(exp));
  };

  const handleActionApplied = async (options: {
    exposure: ExposureModel;
    preferredClosingReason: string;
    otherCommentIfUnavailable: string;
    successMessage: string;
    onSuccess?: (message: string) => void;
    onError?: (message: string) => void;
  }) => {
    const { exposure, preferredClosingReason, otherCommentIfUnavailable, successMessage, onSuccess, onError } = options;
    await onClaimUpdate();

    // Refresh exposures and find the updated exposure
    const updatedExposure = await findExposure((exp) => exp.id === exposure.id);

    if (!updatedExposure) {
      const errorMessage = 'Could not find the exposure after update';
      if (onError) {
        onError(errorMessage);
      }
      return null;
    }

    // Check if exposure can be closed
    const closingExposureDisableReason = getExposureClosingDisabledReasonSimple([updatedExposure]);

    // Only attempt to close the exposure if there's no reason preventing it
    if (closingExposureDisableReason) return;

    try {
      const hasPreferredReason = availableClosingReasons.includes(preferredClosingReason);
      const hasOtherOption = availableClosingReasons.includes('other');

      const reasonStrategies = {
        preferred: () => ({ reason: preferredClosingReason, comment: null }),
        other: () => ({ reason: 'other', comment: otherCommentIfUnavailable }),
        none: () => ({ reason: null, comment: null }),
      } as const;

      let strategyKey: keyof typeof reasonStrategies;
      if (hasPreferredReason) {
        strategyKey = 'preferred';
      } else if (hasOtherOption) {
        strategyKey = 'other';
      } else {
        strategyKey = 'none';
      }

      const { reason, comment } = reasonStrategies[strategyKey]();

      if (reason) {
        await closeExposure(claim.id, updatedExposure.id, reason, comment);
      }
    } catch (error) {
      // If closing fails, we still want to continue with the claim update
      await reportAxiosError(error);
    }

    await onClaimUpdate();
    if (onSuccess) {
      onSuccess(successMessage);
    }

    return updatedExposure;
  };

  const handlePaymentDone = async (
    exposure: ExposureModel,
    onSuccess?: (message: string) => void,
    onError?: (message: string) => void
  ) => {
    return await handleActionApplied({
      exposure,
      preferredClosingReason: 'full_amount_paid', // predefined closing reason key for payment scenarios
      otherCommentIfUnavailable: 'Closed by system due to payment', // comment for 'other'
      successMessage: 'Payment request created', // success message
      onSuccess,
      onError,
    });
  };

  const applyInvoiceItem = async ({
    item,
    documentId,
    claimDocumentSourceId,
    onUpdate,
  }: ApplyInvoiceItemProps): Promise<void> => {
    try {
      await onClaimUpdate();

      const coveragesBreakdown = await getSingleInvoiceCoveragesBreakdown({
        claimId: claim.id,
        documentId,
        claimDocumentSourceId,
      });
      const DetailedCoverageTotals = coveragesBreakdown.coverages.find(
        (coverage) => coverage.coverage_key === item.coverage
      );

      if (!DetailedCoverageTotals) {
        onUpdate({
          status: 'error',
          message: 'Could not find this item in the coverage breakdown',
        });
        return;
      }

      let exposure = await findExposure((exposure) => {
        if (!exposure.coverage) return false;
        return exposure.coverage.id === DetailedCoverageTotals.coverage_id;
      });

      if (!exposure) {
        onUpdate({
          status: 'error',
          message: 'Could not find an exposure for this item',
        });
        return;
      }

      const result = await prepareExposureForPayment(claim.id, exposure.id, DetailedCoverageTotals.covered_amount);
      await onClaimUpdate();
      exposure = await findExposure((exposure) => {
        if (!exposure.coverage) return false;
        return exposure.coverage.id === DetailedCoverageTotals.coverage_id;
      });

      if (!exposure) {
        onUpdate({
          status: 'error',
          message: 'Could not set reserve for exposure, please make the payment from the coverages breakdown page',
        });
        return;
      }

      const onSuccess = (message: string) => onUpdate({ status: 'success', message });
      const onError = (message: string) => onUpdate({ status: 'error', message });

      // Check if payment dialog should open
      if (result.remaining_to_pay > 0) {
        openPaymentDialog({
          add,
          claim,
          exposure,
          onUpdate: async () => {
            if (!exposure) {
              throw new Error('Could not find prepared exposure');
            }
            await handlePaymentDone(exposure, onSuccess, onError);
          },
        });
      } else {
        // No payment needed - try to close with deductible reason
        await handleActionApplied({
          exposure,
          preferredClosingReason: 'payment_below_deductible', // predefined closing reason key for deductible scenarios
          otherCommentIfUnavailable: 'Closed by system due to deductible', // comment for 'other'
          successMessage: 'Deductible applied', // success message
          onSuccess,
          onError,
        });
      }
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      await onClaimUpdate();
    }
  };

  const applyCoveragePayment = async ({ coverageId, onPaymentDone }: ApplyCoveragePaymentProps): Promise<void> => {
    try {
      const { data } = await axios.post<PrepareExposureForAggregativePaymentResponse>(
        `/api/v1/claims/${claim.id}/invoice_based_da/prepare_exposure_for_aggregative_payment`,
        { coverage_id: coverageId }
      );

      const exposure = await findExposure((exp) => exp.id === data.exposure_id);

      if (!exposure) {
        throw new Error('Could not find prepared exposure');
      }

      // Check if payment dialog should open
      if (data.remaining_to_pay > 0) {
        openPaymentDialog({
          add,
          claim,
          exposure,
          onUpdate: async () => {
            await handlePaymentDone(
              exposure,
              () => onPaymentDone(),
              (message) => {
                try {
                  throw new Error(`Error while handling coverage breakdown payment: ${message}`);
                } catch (error) {
                  reportGenericException(error);
                }
              }
            );
          },
        });
      } else {
        // No payment needed - try to close with deductible reason
        await handleActionApplied({
          exposure,
          preferredClosingReason: 'payment_below_deductible', // predefined closing reason key for deductible scenarios
          otherCommentIfUnavailable: 'Closed by system due to deductible', // comment for 'other'
          successMessage: 'Deductible applied', // success message
          onSuccess: () => onPaymentDone(),
          onError: (message) => {
            try {
              throw new Error(`Error while handling coverage breakdown payment: ${message}`);
            } catch (error) {
              reportGenericException(error);
            }
          },
        });
      }
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      await onClaimUpdate();
    }
  };

  return { applyInvoiceItem, applyCoveragePayment, coInsuranceRates, useFetchExposuresProxy: useFetchExposures };
};
